import { useEffect, useMemo, useRef, useState } from 'react'

import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IInAppFeaturesGroupWithAppIcon } from '~/types/iap'
import type { IAppOption } from '~/types/option'

import columns from './columns'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import TableWrapper from '~/components/Tables/TableWrapper'
import { FASTLY_PATH } from '~/const/asset'
import useApps from '~/hooks/useApps'
import { useAppSummary } from '~/providers/AppSummary'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { useFeaturesGroupListQuery } from '~/queries/iap'
import { resize } from '~/utils/media'
import { reactSelectMenuPositionDef } from '~/utils/options'

import './styles.scss'

const ENTRIES_NAME = 'features-goup-list-editor-modal'

const MODAL_ID = 'features-group-list-editor-modal'

interface ModalBodyProps {
  onAddClick: (featuresGroups: IInAppFeaturesGroupWithAppIcon[]) => void
  onClose: VoidFunction
}

const OfferEditorFeaturesTable = ({ onAddClick, onClose }: ModalBodyProps): ReactElement => {
  /** Local state */

  const { appKey, deviceType, isLoading: isAppSummaryLoading } = useAppSummary()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [selectedApp, setSelectedApp] = useState<Pick<IAppOption, 'appKey' | 'deviceType'>>()

  const { appsOptions, isAppsLoading } = useApps()

  const formattedAppsOptions = appsOptions.map((el) => ({
    ...el,
    label: `${el.appKey} - ${el.deviceType}`,
    value: `${el.appKey}/${el.deviceType}`,
  }))

  /** Queries & Mutations */

  const { data: apps } = useAppsManagementListQuery()

  const { data: featuresGroup, isLoading: isFeaturesGroupLoading } = useFeaturesGroupListQuery({
    appKey: selectedApp?.appKey,
    deviceType: selectedApp?.deviceType,
  })

  /** Query dependent state */

  const rowData = useMemo(
    () =>
      featuresGroup?.data?.map((el) => {
        const iconFileId = apps?.find(({ app_key }) => app_key === el.app_key)?.icon_file_id
        return {
          ...el,
          app_icon_url: iconFileId ? resize(`${FASTLY_PATH}${iconFileId}`, 32, 32) : undefined,
        }
      }),
    [apps, featuresGroup?.data]
  )

  const isDataLoading = isAppSummaryLoading || isAppsLoading || isFeaturesGroupLoading

  /** Handlers */

  const handleAppChange = (value: IAppOption | null) => {
    setSelectedApp(value ? { appKey: value.appKey, deviceType: value.deviceType } : undefined)
  }

  const handleAddClick = () => {
    const gridApi = gridRef.current?.api

    if (gridApi) {
      const selectedRows: IInAppFeaturesGroupWithAppIcon[] = gridApi.getSelectedRows()
      onAddClick(selectedRows)
    }
  }

  /** Effects */

  useEffect(() => {
    setSelectedApp({ appKey, deviceType })
  }, [appKey, deviceType])

  /** Render */

  return (
    <>
      <ModalBody>
        {isDataLoading ? (
          <MwMLoader category="item" />
        ) : (
          <>
            <TableWrapper
              buttonComponent={
                <SelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  className="application-select-input"
                  components={{ Option: CustomAppOption }}
                  onChange={(value: unknown) => handleAppChange(value as IAppOption | null)}
                  options={formattedAppsOptions}
                  placeholder="Select an application"
                  value={formattedAppsOptions?.find(
                    ({ appKey, deviceType }) => selectedApp?.deviceType === deviceType && selectedApp?.appKey === appKey
                  )}
                />
              }
              columnDefs={columns}
              entriesName={ENTRIES_NAME}
              ref={gridRef}
              rowData={rowData}
              rowSelection={{
                checkboxes: false,
                enableClickSelection: true,
                enableSelectionWithoutKeys: true,
                headerCheckbox: false,
                mode: 'multiRow',
              }}
              tableRef={tableRef}
              withPagination={false}
              withSearchBar
            />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button onClick={handleAddClick}>Add</Button>
      </ModalFooter>
    </>
  )
}

interface ModalProps extends ModalBodyProps {
  isOpen: boolean
}

const OfferEditorFeaturesGroupModal = ({ isOpen, onAddClick, onClose }: ModalProps): ReactElement => {
  return (
    <Modal className="feature-groups-editor-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add Features Groups</ModalHeader>
      <OfferEditorFeaturesTable onAddClick={onAddClick} onClose={onClose} />
    </Modal>
  )
}

export default OfferEditorFeaturesGroupModal
